import React, { useState, useEffect } from "react";

// Importing bootstrap components
import { Row, Col, Card, Image, Badge } from "react-bootstrap";

// Importing react router dom
import { NavLink, useParams } from "react-router-dom";

// Importing react-redux
import { useDispatch, useSelector } from "react-redux";

// Importing utility
import { FieldCounter } from "utils/FieldCounter";

// Importing slices
import { useGetUserQuery } from "state/slices/user/userApiSlice";
import { useGetWfhsQuery } from "state/slices/wfh/wfhApiSlice";
import { useGetAttendancesByIdQuery } from "state/slices/attendance/attendanceApiSlice";
import { setWfhs } from "state/slices/wfh/wfhSlice";

// Import the HRPagination Component
import WFHTable from "components/WFHTable";

import profilePic from "assets/images/profile.jpg";

// Import utilities
import { isAllowedToDoByKey } from 'permissions-hub';
import { EMPLOYEE_MANAGEMENT, ACCEPTED_PERMISSIONS } from "constants";
import { getAllowedPermissions } from "utils/authentication";
import { formatDate } from "utils/date";

const EmployeeDetails = () => {

  // Get employeeId
  const { employeeId } = useParams();

  const dispatch = useDispatch();

  const { userInfo: loggedUserInfo, } = useSelector((state) => state.auth);

  const isAllowedToUpdateEmployee = isAllowedToDoByKey(
    EMPLOYEE_MANAGEMENT,
    ACCEPTED_PERMISSIONS["update"],
    getAllowedPermissions(loggedUserInfo),
    false
  );

  const wfhs = useSelector((state) => state.wfh.wfhs);

  // Get current date
  const currentDate = new Date();
  const attendanceMonth =
    currentDate.getMonth() !== 0 ? currentDate.getMonth() : 12;
  const attendanceYear =
    currentDate.getMonth() !== 0
      ? currentDate.getFullYear()
      : currentDate.getFullYear() - 1;

  // User query
  const { data: employee, refetch: refetchEmployee, isLoading: isEmployeeLoading } =
    useGetUserQuery(employeeId);

  // WFH query
  const {
    data: wfhsData,
    refetch: refetchWfhs,
    isLoading: isWFHsLoading,
  } = useGetWfhsQuery(employeeId);

  // Attendance query
  const { data: attendancesData, refetch: refetchAttendance, isLoading: isAttendancesLoading } =
    useGetAttendancesByIdQuery({
      year: attendanceYear,
      month: attendanceMonth,
      id: employeeId,
    });

  const isLoading = isEmployeeLoading || isAttendancesLoading;

  const [fieldCounts, setFieldCounts] = useState({
    late: 0,
    ot: 0,
    absent: 0,
  });

  useEffect(() => {
    if (attendancesData) {
      setFieldCounts(FieldCounter(attendancesData));
    }
  }, [attendancesData]);

  useEffect(() => {
    refetchEmployee();
    refetchWfhs();
    if (wfhsData) {
      dispatch(setWfhs(wfhsData.wfhs));
    }
  }, [wfhsData]);

  return (
    <section className="section dashboard">
      {isLoading ? (
        <>
          {/* Profile Header Placeholder */}
          <Row className="mt-lg-5">
            <Col lg={6}>
              <div className="single-profile d-flex align-items-center">
                <div className="all-e-img">
                  <span
                    className="placeholder placeholder-lg rounded-circle"
                    style={{ width: 120, height: 120, display: "block" }}
                  ></span>
                </div>
                <div className="single-con w-100 mx-4">
                  <h4><span className="placeholder col-7 placeholder-lg"></span></h4>
                  <p><span className="placeholder col-4"></span></p>
                  <p><span className="placeholder col-4"></span></p>
                  <p><span className="placeholder col-5"></span></p>
                  <p><span className="placeholder col-6"></span></p>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <Card>
                <Card.Body className="pt-3">
                  <span className="placeholder placeholder-lg col-5 mb-4 d-block"></span>
                  <table className="table table-fixed table-striped table-bordered m-0">
                    <tbody>
                      <tr>
                        <td>
                          <span className="placeholder col-3 d-block mb-2"></span>
                        </td>
                        <td>
                          <span className="placeholder col-6 d-block mb-2"></span>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <span className="placeholder col-4 d-block mb-2"></span>
                        </td>
                        <td>
                          <span className="placeholder col-5 d-block mb-2"></span>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <span className="placeholder col-5 d-block"></span>
                        </td>
                        <td>
                          <span className="placeholder col-7 d-block"></span>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <span className="placeholder col-5 d-block"></span>
                        </td>
                        <td>
                          <span className="placeholder col-7 d-block"></span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <hr />
          {/* Bank Details & Leaves Placeholder */}
          <Row className="mt-lg-5">
            <Col lg={6}>
              <Card>
                <Card.Body className="pt-3">
                  <span className="placeholder placeholder-lg col-5 mb-4 d-block"></span>
                  <table className="table table-fixed table-striped table-bordered m-0">
                    <tbody>
                      <tr>
                        <td>
                          <span className="placeholder col-3 d-block mb-2"></span>
                        </td>
                        <td>
                          <span className="placeholder col-6 d-block mb-2"></span>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <span className="placeholder col-4 d-block mb-2"></span>
                        </td>
                        <td>
                          <span className="placeholder col-5 d-block mb-2"></span>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <span className="placeholder col-5 d-block"></span>
                        </td>
                        <td>
                          <span className="placeholder col-7 d-block"></span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={6}>
              <Card>
                <Card.Body className="pt-3">
                  <span className="placeholder placeholder-lg col-5 mb-4 d-block"></span>
                  <table className="table table-fixed table-striped table-bordered m-0">
                    <tbody>
                      <tr>
                        <td>
                          <span className="placeholder col-3 d-block mb-2"></span>
                        </td>
                        <td>
                          <span className="placeholder col-6 d-block mb-2"></span>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <span className="placeholder col-4 d-block mb-2"></span>
                        </td>
                        <td>
                          <span className="placeholder col-5 d-block mb-2"></span>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <span className="placeholder col-5 d-block"></span>
                        </td>
                        <td>
                          <span className="placeholder col-7 d-block"></span>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <span className="placeholder col-3 d-block mb-2"></span>
                        </td>
                        <td>
                          <span className="placeholder col-6 d-block mb-2"></span>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <span className="placeholder col-4 d-block mb-2"></span>
                        </td>
                        <td>
                          <span className="placeholder col-5 d-block mb-2"></span>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <span className="placeholder col-5 d-block"></span>
                        </td>
                        <td>
                          <span className="placeholder col-7 d-block"></span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <span className="placeholder bg-primary col-3 mt-3" style={{ height: "38px", display: "block" }}></span>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      ) : (
        <>
          <Row className="mt-lg-5">
            <Col lg={6}>
              <div className="single-profile d-flex align-items-center">
                <div className="all-e-img">
                  <Image
                    src={
                      employee?.biometricDetails?.profilePicture?.length > 0
                        ? employee.biometricDetails.profilePicture
                        : profilePic
                    }
                    onError={e => {
                      e.target.src = profilePic;
                    }}
                    alt="Profile"
                  />
                </div>
                <div className="single-con mx-4">
                  <h4>{employee?.name}</h4>
                  <p>
                    <i>{employee?.designation?.name}</i>
                  </p>
                  <p>
                    <strong>Team: </strong>
                    <i>{employee?.team?.name}</i>
                  </p>
                  <p>
                    <strong>Date of Joining: </strong>
                    {formatDate(employee.dateOfJoining, 'dd LLL, yyyy')}
                  </p>
                  <p className="mb-0">
                    <strong>Employee ID: </strong>
                    {employee?.employeeId}
                  </p>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <Card>
                <Card.Body>
                  <h5 className="pt-3 mb-3">Personal Details</h5>
                  <table className="table table-striped table-bordered table-fixed m-0">
                    <tbody>
                      <tr>
                        <th>Phone Number</th>
                        <td>{employee?.phone ? <a href={`tel:+91${employee.phone}`}>{"+91 " + employee.phone}</a> : <Badge bg="warning" text="dark">Pending</Badge>}</td>
                      </tr>
                      <tr>
                        <th>Email Address</th>
                        <td>{employee?.email ? <a href={`mailto:${employee.email}`}>{employee.email}</a> : <Badge bg="warning" text="dark">Pending</Badge>}</td>
                      </tr>
                      <tr>
                        <th>Date of Birth</th>
                        <td>{employee?.dateOfBirth ? formatDate(employee.dateOfBirth, 'dd LLL, yyyy') : <Badge bg="warning" text="dark">Pending</Badge>}</td>
                      </tr>
                      <tr>
                        <th>Address</th>
                        <td>{employee?.address ? employee.address : <Badge bg="warning" text="dark">Pending</Badge>}</td>
                      </tr>
                    </tbody>
                  </table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <hr />
          <Row className="mt-lg-5">
            <Col lg={6}>
              <Card>
                <Card.Body>
                  <h5 className="pt-3 mb-3">Salary Account Details</h5>
                  <table className="table table-striped table-bordered table-fixed m-0">
                    <tbody>
                      <tr>
                        <th>Bank Name</th>
                        <td>
                          {employee?.bankDetails?.bankName ? employee.bankDetails.bankName : <Badge bg="warning" text="dark">Pending</Badge>}
                        </td>
                      </tr>
                      <tr>
                        <th>Account No</th>
                        <td>
                          {employee?.bankDetails?.accountNumber ? employee.bankDetails.accountNumber : <Badge bg="warning" text="dark">Pending</Badge>}
                        </td>
                      </tr>
                      <tr>
                        <th>IFSC Code</th>
                        <td>
                          {employee?.bankDetails?.ifscCode ? employee.bankDetails.ifscCode : <Badge bg="warning" text="dark">Pending</Badge>}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={6}>
              <Card>
                <Card.Body>
                  <h5 className="pt-3 mb-3">Leave Summary</h5>
                  <table className="table table-striped table-bordered table-fixed m-0">
                    <tbody>
                      <tr>
                        <th>Pending CL</th>
                        <td>{employee?.cl ? employee.cl : 0}</td>
                      </tr>
                      <tr>
                        <th>Total Lates</th>
                        <td>{fieldCounts.late}</td>
                      </tr>
                      <tr>
                        <th>Total OT's</th>
                        <td>{fieldCounts.ot}</td>
                      </tr>
                      <tr>
                        <th>Total Half Day's</th>
                        <td>{fieldCounts.halfDay}</td>
                      </tr>
                      <tr>
                        <th>Total Deduction Days</th>
                        <td>{employee?.totalDD ? employee.totalDD : 0}</td>
                      </tr>
                      <tr>
                        <th>Total Absents</th>
                        <td>{fieldCounts.absent}</td>
                      </tr>
                    </tbody>
                  </table>
                  <NavLink
                    to={`/attendances/${employee?.employeeId}`}
                    className="btn btn-primary mt-3"
                  >
                    View Attendance
                  </NavLink>
                </Card.Body>
              </Card>
            </Col>
            <Col sm={12}>
              <Card>
                <Card.Body>
                  <h5 className="pt-3 mb-3">WFH Log</h5>
                  {loggedUserInfo?.employeeId === employeeId ? (
                    <NavLink to="/wfh">Manage</NavLink>
                  ) : (
                    <WFHTable
                      wfhs={wfhs}
                      isAllowedToDelete={isAllowedToUpdateEmployee}
                      refetch={refetchWfhs}
                      isWFHsLoading={isWFHsLoading}
                      showSelfies={true}
                    />
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </section>
  );
};

export default EmployeeDetails;
